import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { clearAlphaIbkrAccountDetailsDraft } from '../pages/alpha/alphaIbkrAccountDetailsDraft';
import { clearAlphaRiskProfileDraft } from '../pages/alpha/alphaRiskProfileDraft';
import { clearAlphaAllocationDraft } from './alphaAllocationDraftStorage';
import { clearStrategyDraftStorage } from "./strategyDraftStorage";
import { analyticsService } from '../services/analyticsService';
import { trackAccountCreatedIfRecent } from '../services/authAccountAnalytics';
import authClient from '../services/auth-client';
import { getValidatedPostHogDeviceId } from '../utils/posthogDeviceId';
import { completeOAuthLogin } from '../services/oauthService';
import httpService, { type AuthTokenPair } from '../services/httpService';
import profileConfigService, { ProfileConfig } from '../services/profileConfigService';
import userAccountContextService, {
  type GetAccountsResponse,
  type UserAccountSummary,
} from "../services/userAccountContextService";
import { trackingService } from '../services/trackingService';
import { TrackingEvents } from '../services/trackingEvents';
import { trackAuth, trackError } from '../utils/analytics';
import { navigateToOAuthResponseUrl } from '../utils/oauthContinuation';
import { saveLastLoginPreference } from '../utils/lastLoginPreference';
import { startTokenRefresh, stopTokenRefresh } from '../utils/tokenRefresh';
import { isCustomerViewBootProfile } from '../utils/adminCustomerViewBoot';
import {
  clearAdminCustomerViewRouteStore,
  reconcileAdminCustomerViewRouteStoreForActor,
} from '../utils/adminCustomerViewRouteSession';

// Better Auth User type (matches the backend)
export interface User {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  emailVerified: boolean;
  roles?: string[];
  providerId?: string;
  userRoles?: Array<{
    id: string;
    userId: string;
    role: string;
    createdAt: string;
    updatedAt: string;
  }>;
  userProviders?: Array<{
    id: string;
    userId: string;
    providerId: string;
    createdAt: string;
    updatedAt: string;
    providers: {
      id: string;
      name: string;
      createdAt: string;
      updatedAt: string;
    };
  }>;
  provider?: {
    id: string;
    name: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface AuthContextType {
  user: User | null;
  /** Authenticated session owner. Roles and security authority always belong to this user. */
  actorUserId: string | null;
  /** Account owner whose domain data the current browser-wide token pair selects. */
  effectiveUserId: string | null;
  accounts: UserAccountSummary[];
  isAccountContextLoading: boolean;
  isAccountSwitching: boolean;
  userConfig: ProfileConfig | null;
  /** True once userConfig has been resolved (including 404 → null). False if the fetch failed or hasn't run. */
  userConfigLoaded: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isAdmin: () => boolean;
  getDefaultLandingPage: (origin?: "homepage") => string;
  sendOtp: (email: string, flow?: "login" | "signup") => Promise<{ success: boolean; message: string }>;
  verifyOtp: (email: string, otp: string, flow?: "login" | "signup", oauthQuery?: string) => Promise<LoginResponse>;
  signInWithGoogle: (idToken: string, accessToken?: string, oauthQuery?: string) => Promise<LoginResponse>;
  getUserConfigValue: (key: string) => string | null;
  refreshUserConfig: () => Promise<void>;
  refreshAccounts: () => Promise<void>;
  activateAccount: (targetUserId: string) => Promise<void>;
  runInActorAccountContext: <T>(operation: () => Promise<T>) => Promise<T>;
}

export interface LoginResponse {
  success: boolean;
  redirected?: boolean;
  message?: string;
  user?: User;
  token?: string;
  accessToken?: string;
  refreshToken?: string;
}

interface SocialSignInResponse {
  data?: {
    accessToken?: string;
    refreshToken?: string;
    token?: string;
    user?: any;
    session?: {
      accessToken?: string;
      refreshToken?: string;
      token?: string;
      user?: any;
    };
  };
  error?: {
    message?: string;
    status?: number;
    code?: string;
    deleted?: boolean;
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function coerceUserConfigValue(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (typeof value === "boolean" || typeof value === "number") return String(value);
  return null;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function isAccountScopedQueryKey(queryKey: QueryKey): boolean {
  return queryKey[0] === "account"
    || (queryKey[0] === "actual-performance" && queryKey[1] === "self");
}

function isAccountContextAccessDenied(error: unknown): boolean {
  const candidate = error as {
    message?: unknown;
    status?: unknown;
    response?: { status?: unknown };
  };
  return (candidate.status === 403 || candidate.response?.status === 403)
    && candidate.message === "Account context access denied";
}

function authTokenPairsEqual(
  left: AuthTokenPair | null,
  right: AuthTokenPair | null
): boolean {
  if (left === null || right === null) return left === right;
  return left.accessToken === right.accessToken
    && left.refreshToken === right.refreshToken;
}

function normalizeAuthenticatedUser(userData: any): User {
  const normalized = { ...userData };
  if (normalized.userProviders?.length) {
    normalized.providerId = normalized.userProviders[0].providerId;
  }
  if (!normalized.roles?.length) {
    normalized.roles = normalized.userRoles?.length
      ? normalized.userRoles.map((userRole: { role: string }) => userRole.role)
      : [];
  }
  return normalized as User;
}

type InitialTokenRefreshResult =
  | { outcome: "success" | "failed"; tokens: AuthTokenPair | null }
  | { outcome: "invalidated" };

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const customerViewBoot = isCustomerViewBootProfile();
  const [user, setUser] = useState<User | null>(null);
  const [userConfig, setUserConfig] = useState<ProfileConfig | null>(null);
  const [userConfigLoaded, setUserConfigLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<UserAccountSummary[]>([]);
  const [effectiveUserId, setEffectiveUserId] = useState<string | null>(null);
  const [isAccountContextLoading, setIsAccountContextLoading] = useState<boolean>(false);
  const [isAccountSwitching, setIsAccountSwitching] = useState<boolean>(false);
  const previousEffectiveUserIdRef = useRef<string | null>(null);
  const effectiveUserIdRef = useRef<string | null>(null);
  const userRef = useRef<User | null>(null);
  const accountTransitionRef = useRef(false);
  const accountTransitionOwnerSequenceRef = useRef(0);
  const accountTransitionOwnerRef = useRef<number | null>(null);
  const accountContextGenerationRef = useRef(0);
  const userConfigRequestRef = useRef(0);
  const authInitializationInFlightRef = useRef(true);
  const externalTokenReconcilePendingRef = useRef(false);
  const externalHttpTransitionRef = useRef(false);
  const externalTransitionTokenPairRef = useRef<AuthTokenPair | null>(null);
  // A second reset would create another anonymous ID and strand pre-login events.
  const analyticsIdentityResetSinceLastIdentifyRef = useRef(false);
  const staleRecoveryPromiseRef = useRef<Promise<void> | null>(null);
  const reconcileExternalTokensRef = useRef<() => Promise<void>>(async () => undefined);

  effectiveUserIdRef.current = effectiveUserId;
  userRef.current = user;

  const resetAnalyticsIdentity = (): void => {
    if (analyticsIdentityResetSinceLastIdentifyRef.current) return;
    trackingService.reset();
    analyticsIdentityResetSinceLastIdentifyRef.current = true;
  };

  const loadUserConfigForCurrentBoot = (
    requestScope?: "account-transition" | "session-transition"
  ): Promise<ProfileConfig | null> => {
    if (customerViewBoot) return Promise.resolve(null);
    return requestScope
      ? profileConfigService.getUserConfig({ requestScope })
      : profileConfigService.getUserConfig();
  };

  const settleCustomerViewConfig = (): void => {
    if (!customerViewBoot) return;
    setUserConfig(null);
    setUserConfigLoaded(true);
  };

  const clearAccountScopedClientState = async (ownerUserId: string | null): Promise<void> => {
    const predicate = (query: { queryKey: QueryKey }) => isAccountScopedQueryKey(query.queryKey);
    try {
      await queryClient.cancelQueries({ predicate });
    } catch (error) {
      console.error("Failed to cancel account-scoped queries:", error);
    }
    queryClient.removeQueries({ predicate });
    clearAlphaIbkrAccountDetailsDraft(ownerUserId);
    clearAlphaRiskProfileDraft(ownerUserId);
    clearAlphaAllocationDraft(ownerUserId);
    clearStrategyDraftStorage();
  };

  const blockUnverifiedAccountContext = async (
    ownerUserId: string | null
  ): Promise<void> => {
    setAccounts([]);
    setEffectiveUserId(null);
    setUserConfig(null);
    setUserConfigLoaded(customerViewBoot);
    await clearAccountScopedClientState(ownerUserId);
  };

  const isAccountTransitionInvalidated = (generation: number): boolean => (
    accountContextGenerationRef.current !== generation
    || httpService.hasUnadoptedExternalAuthTokenChange()
  );

  const runInteractiveAuthTransition = async <T,>(
    sourceGeneration: number,
    sourceTokens: AuthTokenPair | null,
    nextTokens: AuthTokenPair,
    operation: (
      generation: number,
      prepareAnalyticsIdentity: (nextActorUserId: string) => void
    ) => Promise<T>
  ): Promise<T> => {
    if (
      accountContextGenerationRef.current !== sourceGeneration
      || !authTokenPairsEqual(httpService.getAuthTokenPair(), sourceTokens)
      || httpService.hasUnadoptedExternalAuthTokenChange()
      || accountTransitionRef.current
      || externalTokenReconcilePendingRef.current
    ) {
      throw new Error("Authentication changed in another browser tab");
    }

    const previousActorUserId = userRef.current?.id ?? null;
    const previousEffectiveUserId = effectiveUserIdRef.current;
    const transitionGeneration = ++accountContextGenerationRef.current;
    const transitionOwner = claimAccountTransition();
    httpService.beginAccountContextTransition();
    httpService.beginAuthSessionTransition();
    setIsAccountSwitching(true);
    stopTokenRefresh();
    let succeeded = false;
    let installed = false;
    let analyticsIdentityPrepared = false;
    try {
      httpService.replaceAuthTokenPair(nextTokens);
      installed = true;
      await blockUnverifiedAccountContext(previousEffectiveUserId);
      if (isAccountTransitionInvalidated(transitionGeneration)) {
        throw new Error("Authentication changed in another browser tab");
      }
      queryClient.clear();
      clearAdminCustomerViewRouteStore();
      userRef.current = null;
      setUser(null);

      const result = await operation(
        transitionGeneration,
        (nextActorUserId) => {
          const shouldReset = previousActorUserId === null
            ? sourceTokens !== null
              && !analyticsIdentityResetSinceLastIdentifyRef.current
            : previousActorUserId !== nextActorUserId;
          if (shouldReset) resetAnalyticsIdentity();
          analyticsIdentityPrepared = true;
        }
      );
      if (isAccountTransitionInvalidated(transitionGeneration)) {
        throw new Error("Authentication changed in another browser tab");
      }
      if (
        !analyticsIdentityPrepared
        && (previousActorUserId !== null || sourceTokens !== null)
      ) {
        resetAnalyticsIdentity();
      }
      succeeded = true;
      return result;
    } finally {
      if (
        installed
        && !succeeded
        && !isAccountTransitionInvalidated(transitionGeneration)
      ) {
        httpService.clearAuthTokenPair();
        if (
          previousActorUserId !== null
          || sourceTokens !== null
          || analyticsIdentityPrepared
        ) {
          resetAnalyticsIdentity();
        }
      }
      httpService.finishAuthSessionTransition();
      finishAccountTransition(succeeded, true, true, transitionOwner);
    }
  };

  const startAuthTokenRefresh = (): void => {
    startTokenRefresh(
      (tokens) => httpService.updateAuthTokens(tokens.accessToken, tokens.refreshToken),
      (error) => {
        trackError(`Token refresh failed: ${error}`, 'AuthContext.tokenRefresh');
        void recoverFromStaleAccountContext();
      }
    );
  };

  const beginExternalHttpTransition = (): void => {
    const tokenPair = httpService.getAuthTokenPair();
    if (externalHttpTransitionRef.current) {
      if (authTokenPairsEqual(externalTransitionTokenPairRef.current, tokenPair)) return;

      // Keep the existing external-transition lease while rotating the HTTP
      // generation. This aborts bootstrap work for an earlier browser-wide
      // token pair without briefly reopening ordinary account requests.
      httpService.beginAccountContextTransition();
      httpService.finishAccountContextTransition();
      httpService.beginAuthSessionTransition();
      httpService.finishAuthSessionTransition();
      externalTransitionTokenPairRef.current = tokenPair;
      return;
    }
    httpService.beginAccountContextTransition();
    httpService.beginAuthSessionTransition();
    externalHttpTransitionRef.current = true;
    externalTransitionTokenPairRef.current = tokenPair;
  };

  const finishExternalHttpTransition = (): void => {
    if (!externalHttpTransitionRef.current) return;
    externalHttpTransitionRef.current = false;
    externalTransitionTokenPairRef.current = null;
    httpService.finishAccountContextTransition();
    httpService.finishAuthSessionTransition();
  };

  const claimAccountTransition = (): number => {
    const owner = accountTransitionOwnerSequenceRef.current + 1;
    accountTransitionOwnerSequenceRef.current = owner;
    accountTransitionOwnerRef.current = owner;
    accountTransitionRef.current = true;
    return owner;
  };

  const finishAccountTransition = (
    restartTokenRefresh: boolean,
    releaseSwitching: boolean,
    releaseHttpTransition: boolean,
    transitionOwner: number
  ): void => {
    if (releaseHttpTransition) {
      // Every overlapping stale/external path owns another depth-counted lease,
      // so releasing this completed local path cannot reopen account traffic.
      httpService.finishAccountContextTransition();
    }
    if (accountTransitionOwnerRef.current !== transitionOwner) return;
    accountTransitionOwnerRef.current = null;
    accountTransitionRef.current = false;
    if (staleRecoveryPromiseRef.current) return;
    if (externalTokenReconcilePendingRef.current) {
      Promise.resolve().then(() => {
        void reconcileExternalTokensRef.current();
      });
      return;
    }
    finishExternalHttpTransition();
    if (restartTokenRefresh && userRef.current && httpService.getAuthTokenPair()) {
      startAuthTokenRefresh();
    }
    if (releaseSwitching) setIsAccountSwitching(false);
  };

  const recoverFromStaleAccountContext = (): Promise<void> => {
    if (staleRecoveryPromiseRef.current) return staleRecoveryPromiseRef.current;

    httpService.beginAccountContextTransition();
    httpService.beginAuthSessionTransition();
    const recoveryGeneration = ++accountContextGenerationRef.current;
    const recoveryPromise = (async () => {
      setIsAccountSwitching(true);
      stopTokenRefresh();
      try {
        await clearAccountScopedClientState(effectiveUserIdRef.current);
        if (
          accountContextGenerationRef.current !== recoveryGeneration
          || httpService.hasUnadoptedExternalAuthTokenChange()
        ) return;
        queryClient.clear();
        httpService.clearAuthTokenPair();
        setAccounts([]);
        setEffectiveUserId(null);
        setIsAccountContextLoading(false);
        clearAdminCustomerViewRouteStore();
        setUser(null);
        setUserConfig(null);
        setUserConfigLoaded(false);
        resetAnalyticsIdentity();
      } finally {
        httpService.finishAccountContextTransition();
        httpService.finishAuthSessionTransition();
        staleRecoveryPromiseRef.current = null;
        if (!accountTransitionRef.current) {
          if (externalTokenReconcilePendingRef.current) {
            Promise.resolve().then(() => {
              void reconcileExternalTokensRef.current();
            });
          } else {
            finishExternalHttpTransition();
            setIsAccountSwitching(false);
          }
        }
      }
    })();
    staleRecoveryPromiseRef.current = recoveryPromise;
    return recoveryPromise;
  };

  const loadAccountContext = async (
    expectedActorUserId: string,
    publish = true,
    requestScope: "actor" | "account-transition" | "session-transition" = "actor"
  ): Promise<GetAccountsResponse> => {
    setIsAccountContextLoading(true);
    try {
      const accountContext = await userAccountContextService.getAccounts({ requestScope });
      if (accountContext.userId !== expectedActorUserId) {
        throw new Error("Account context actor does not match the authenticated user");
      }
      if (publish) {
        setAccounts(accountContext.list);
        setEffectiveUserId(accountContext.effectiveUserId);
      }
      return accountContext;
    } finally {
      setIsAccountContextLoading(false);
    }
  };

  const publishAuthenticatedUser = (nextUser: User): void => {
    // Reconcile before publishing the actor so no Customer View descendant can
    // resolve a mapping left by another authenticated session in this tab.
    reconcileAdminCustomerViewRouteStoreForActor(nextUser.id);
    userRef.current = nextUser;
    setUser(nextUser);
  };

  useEffect(() => {
    const previousUserId = previousEffectiveUserIdRef.current;
    if (previousUserId && previousUserId !== effectiveUserId) {
      clearAlphaIbkrAccountDetailsDraft(previousUserId);
      clearAlphaRiskProfileDraft(previousUserId);
      clearAlphaAllocationDraft(previousUserId);
    }
    previousEffectiveUserIdRef.current = effectiveUserId;
  }, [effectiveUserId]);

  useEffect(() => {
    httpService.setAccountContextAccessDeniedHandler(() => {
      void recoverFromStaleAccountContext();
    });
    return () => httpService.setAccountContextAccessDeniedHandler(null);
  }, []);

  // Initialize authentication state on mount
  useEffect(() => {
    authInitializationInFlightRef.current = true;
    const isCurrentInitialization = (
      generation: number,
      tokens: AuthTokenPair | null
    ): boolean => accountContextGenerationRef.current === generation
      && authTokenPairsEqual(httpService.getAuthTokenPair(), tokens);

    const completeCurrentInitialization = (): void => {
      externalTokenReconcilePendingRef.current = false;
      finishExternalHttpTransition();
      setIsAccountSwitching(false);
    };

    const attemptTokenRefresh = async (
      initializationGeneration: number,
      initialTokens: AuthTokenPair | null,
      configRequestScope?: "session-transition"
    ): Promise<InitialTokenRefreshResult> => {
      let expectedTokens = initialTokens;
      try {
        const response = await httpService.refreshToken<{
          accessToken?: string;
          refreshToken?: string;
          token?: string;
        }>({ requestScope: configRequestScope ?? "actor" });
        if (!isCurrentInitialization(initializationGeneration, expectedTokens)) {
          return { outcome: "invalidated" };
        }

        if (response.status === 200) {
          const data = response.data;
          const newAccessToken = data.accessToken || data.token;
          const newRefreshToken = data.refreshToken;
          if (newAccessToken) {
            httpService.updateAuthTokens(newAccessToken, newRefreshToken);
            const refreshedTokens = httpService.getAuthTokenPair();
            if (!refreshedTokens) return { outcome: "failed", tokens: null };
            expectedTokens = refreshedTokens;
            if (!isCurrentInitialization(initializationGeneration, refreshedTokens)) {
              return { outcome: "invalidated" };
            }

            // Now fetch user data with the refreshed access token.
            const userResponse = await httpService.getAuth<{user: any}>(
              '/validate',
              configRequestScope ? { requestScope: configRequestScope } : undefined
            );
            if (!isCurrentInitialization(initializationGeneration, refreshedTokens)) {
              return { outcome: "invalidated" };
            }

            if (userResponse.status === 200) {
              const userData = userResponse.data?.user || {};
              if (userData.userProviders && userData.userProviders.length > 0) {
                userData.providerId = userData.userProviders[0].providerId;
              }

              // Normalize roles: ensure user.roles is always populated.
              if (!userData.roles || userData.roles.length === 0) {
                if (userData.userRoles && userData.userRoles.length > 0) {
                  userData.roles = userData.userRoles.map((ur: any) => ur.role);
                } else {
                  userData.roles = [];
                }
              }

              // Fetch effective-user configuration and account context.
              settleCustomerViewConfig();
              try {
                const [config, accountContext] = await Promise.all([
                  loadUserConfigForCurrentBoot(configRequestScope),
                  loadAccountContext(
                    userData.id,
                    false,
                    configRequestScope ?? "actor"
                  ),
                ]);
                if (!isCurrentInitialization(initializationGeneration, refreshedTokens)) {
                  return { outcome: "invalidated" };
                }
                setAccounts(accountContext.list);
                setEffectiveUserId(accountContext.effectiveUserId);
                setUserConfig(config);
                setUserConfigLoaded(true);
              } catch (configError) {
                if (!isCurrentInitialization(initializationGeneration, refreshedTokens)) {
                  return { outcome: "invalidated" };
                }
                console.error('Failed to initialize effective-user context:', configError);
              }

              publishAuthenticatedUser(userData as User);

              // Identify user across analytics tools (GA, PostHog).
              trackingService.identify(userData.id, {
                email: userData.email,
                name: userData.name,
                role: userData.roles?.[0] || "investor",
                isProvider: userData.roles?.includes("PROVIDER") || false,
                isAdmin: userData.roles?.includes("ADMIN") || false,
                providerId: userData.providerId,
                createdAt: userData.createdAt,
              });
              analyticsIdentityResetSinceLastIdentifyRef.current = false;

              startAuthTokenRefresh();
              return { outcome: "success", tokens: refreshedTokens };
            }
          }
        }
        return { outcome: "failed", tokens: expectedTokens };
      } catch (error) {
        return isCurrentInitialization(initializationGeneration, expectedTokens)
          ? { outcome: "failed", tokens: expectedTokens }
          : { outcome: "invalidated" };
      }
    };

    const initializeAuth = async (
      configRequestScope?: "session-transition"
    ) => {
      const initializationGeneration = accountContextGenerationRef.current;
      const initializationTokens = httpService.getAuthTokenPair();
      const restartAfterExternalTokenChange = async (): Promise<void> => {
        beginExternalHttpTransition();
        httpService.notifyExternalAuthTokenChange();
        try {
          await blockUnverifiedAccountContext(effectiveUserIdRef.current);
          await initializeAuth("session-transition");
        } finally {
          if (!externalTokenReconcilePendingRef.current) {
            finishExternalHttpTransition();
          }
        }
      };

      try {
        // Validate the current access token by fetching user data
        const response = await httpService.getAuth<{user: any}>(
          '/validate',
          configRequestScope ? { requestScope: configRequestScope } : undefined
        );

        if (!isCurrentInitialization(initializationGeneration, initializationTokens)) {
          return await restartAfterExternalTokenChange();
        }

        if (response.status === 200) {
          const userData = response.data?.user || {};

          // Extract providerId from userProviders array if available
          if (userData.userProviders && userData.userProviders.length > 0) {
            userData.providerId = userData.userProviders[0].providerId;
          }

          // Normalize roles: ensure user.roles is always populated
          if (!userData.roles || userData.roles.length === 0) {
            if (userData.userRoles && userData.userRoles.length > 0) {
              userData.roles = userData.userRoles.map((ur: any) => ur.role);
            } else {
              userData.roles = [];
            }
          }

          // Fetch user configuration (key-value pairs set by admin)
          settleCustomerViewConfig();
          try {
            const [config, accountContext] = await Promise.all([
              loadUserConfigForCurrentBoot(configRequestScope),
              loadAccountContext(
                userData.id,
                false,
                configRequestScope ?? "actor"
              ),
            ]);
            if (!isCurrentInitialization(initializationGeneration, initializationTokens)) {
              return await restartAfterExternalTokenChange();
            }
            setAccounts(accountContext.list);
            setEffectiveUserId(accountContext.effectiveUserId);
            setUserConfig(config);
            setUserConfigLoaded(true);
          } catch (configError) {
            if (!isCurrentInitialization(initializationGeneration, initializationTokens)) {
              return await restartAfterExternalTokenChange();
            }
            // Keep non-account portals available, but Alpha remains behind the
            // unresolved account-context boundary until a retry succeeds.
            console.error('Failed to initialize effective-user context:', configError);
          }

          publishAuthenticatedUser(userData as User);

          // Identify user across analytics tools (GA, PostHog).
          // trackingService.identify also recovers persisted capture state.
          trackingService.identify(userData.id, {
            email: userData.email,
            name: userData.name,
            role: userData.roles?.[0] || "investor",
            isProvider: userData.roles?.includes("PROVIDER") || false,
            isAdmin: userData.roles?.includes("ADMIN") || false,
            providerId: userData.providerId,
            createdAt: userData.createdAt,
          });
          analyticsIdentityResetSinceLastIdentifyRef.current = false;

          // Start token refresh schedule for authenticated user
          startAuthTokenRefresh();
          completeCurrentInitialization();
        } else if (response.status === 401) {
          // Access token is expired, try to refresh
          const refreshResult = await attemptTokenRefresh(
            initializationGeneration,
            initializationTokens,
            configRequestScope
          );
          if (refreshResult.outcome === "invalidated") {
            return await restartAfterExternalTokenChange();
          }
          if (refreshResult.outcome === "success") {
            completeCurrentInitialization();
          } else if (isCurrentInitialization(initializationGeneration, refreshResult.tokens)) {
            // Refresh failed, clear tokens and analytics identity.
            // Reset identity and persisted analytics state for the next session on this device.
            httpService.clearAuthTokenPair();
            resetAnalyticsIdentity();
          } else {
            return await restartAfterExternalTokenChange();
          }
        } else {
          if (!isCurrentInitialization(initializationGeneration, initializationTokens)) {
            return await restartAfterExternalTokenChange();
          }
          // Other error, clear tokens and analytics identity.
          // Same rationale as the 401-refresh-fail branch above: any
          // code path that transitions the auth state to "no user" must
          // clear analytics opt-out state so a new user on the same
          // browser is tracked correctly.
          httpService.clearAuthTokenPair();
          resetAnalyticsIdentity();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Network error, keep tokens but don't set user (they can try again)
      } finally {
        authInitializationInFlightRef.current = false;
        setIsLoading(false);
        if (externalTokenReconcilePendingRef.current) {
          Promise.resolve().then(() => {
            void reconcileExternalTokensRef.current();
          });
        }
      }
    };

    var token = localStorage.getItem('plutus_auth_access_token');
    if (token) {
      initializeAuth();
    } else {
      authInitializationInFlightRef.current = false;
      setIsLoading(false);
    }
  }, []);

  // Cleanup token refresh on component unmount
  useEffect(() => {
    return () => {
      stopTokenRefresh();
    };
  }, []);

  const sendOtp = async (
    email: string,
    flow: "login" | "signup" = "login"
  ): Promise<{ success: boolean; message: string }> => {
    // sendOtp is shared by the login and signup entry forms in AuthPageV2.
    // Both hit the same `/send-otp` endpoint; the backend auto-provisions
    // new users on first verify, so client-side captcha and the legacy
    // `/signup` endpoint are no longer required. Emit the funnel event
    // that matches the caller's flow so AUTH_LOGIN_* and AUTH_SIGNUP_*
    // funnels stay mutually exclusive.
    const sentEvent =
      flow === "signup" ? TrackingEvents.AUTH_SIGNUP_OTP_SENT : TrackingEvents.AUTH_LOGIN_OTP_SENT;
    const errorEvent =
      flow === "signup" ? TrackingEvents.AUTH_SIGNUP_ERROR : TrackingEvents.AUTH_LOGIN_ERROR;

    try {
      const response = await httpService.post<{message: string}>('/send-otp', { email }, {
        baseUrlType: 'auth',
        requiresAuth: false
      });
      const data = response.data;

      trackAuth('send-otp', 'send-otp');
      trackingService.track(sentEvent, {
        email_domain: email.split('@')[1] || 'unknown',
      });
      return { success: true, message: data.message };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      trackError(`OTP sending failed: ${errorMessage}`, 'AuthV2Context.sendOtp');
      // Emit the flow-specific error event so send-stage drop-off is
      // visible in the correct funnel. `stage` distinguishes this from
      // verify-stage failures.
      trackingService.track(errorEvent, {
        stage: 'send_otp',
        reason: 'send_otp_threw',
        email_domain: email.split('@')[1] || 'unknown',
      });
      return { success: false, message: errorMessage };
    }
  };

  const verifyOtp = async (email: string, otp: string, flow: "login" | "signup" = "login", oauthQuery?: string): Promise<LoginResponse> => {
    // verifyOtp is shared between SignupPage.handleOTPSubmit and
    // OTPLoginPage.handleOTPSubmit. Emit the error event that matches
    // the caller's flow so signup-OTP failures do not pollute the
    // login-error cohort (and vice-versa).
    const errorEvent = flow === "signup" ? TrackingEvents.AUTH_SIGNUP_ERROR : TrackingEvents.AUTH_LOGIN_ERROR;
    const sourceGeneration = accountContextGenerationRef.current;
    const sourceTokens = httpService.getAuthTokenPair();
    try {
      const anonymousId = getValidatedPostHogDeviceId();
      const loginRequest = { email, otp, ...(anonymousId ? { anonymousId } : {}) };
      const oauthLogin = oauthQuery === undefined
        ? undefined
        : await completeOAuthLogin("/verify-otp", loginRequest, oauthQuery);
      const response = oauthLogin ? { data: oauthLogin } : await httpService.post<{token: string, refreshToken: string, user?: any, accessToken?: string}>('/verify-otp', loginRequest, {
        baseUrlType: 'auth', 
        requiresAuth: false 
      });
      const data = response.data;

      const accessToken = data.accessToken || ("token" in data ? data.token : undefined);
      const refreshToken = data.refreshToken;

      if (!accessToken || !refreshToken) {
        trackError(`OTP validation failed: No access token or refresh token`, 'AuthV2Context.verifyOtp');
        trackingService.track(errorEvent, { reason: 'no_access_or_refresh_token', flow });
        return { success: false, message: 'Invalid OTP' };
      }

      const loginResult = await runInteractiveAuthTransition(
        sourceGeneration,
        sourceTokens,
        { accessToken, refreshToken },
        async (transitionGeneration, prepareAnalyticsIdentity) => {
          const userData = data.user ? { ...data.user } : null;
          if (userData?.userProviders?.length) {
            userData.providerId = userData.userProviders[0].providerId;
          }
          if (userData && (!userData.roles || userData.roles.length === 0)) {
            userData.roles = userData.userRoles?.length
              ? userData.userRoles.map((ur: any) => ur.role)
              : [];
          }

          // TODO Mitren We need to remove the any designation from here.
          if (userData) {
            settleCustomerViewConfig();
            try {
              const [config, accountContext] = await Promise.all([
                loadUserConfigForCurrentBoot("session-transition"),
                loadAccountContext(userData.id, false, "session-transition"),
              ]);
              if (isAccountTransitionInvalidated(transitionGeneration)) {
                throw new Error("Authentication changed in another browser tab");
              }
              setAccounts(accountContext.list);
              setEffectiveUserId(accountContext.effectiveUserId);
              setUserConfig(config);
              setUserConfigLoaded(true);
            } catch (configError) {
              if (isAccountTransitionInvalidated(transitionGeneration)) {
                throw configError;
              }
              console.error('Failed to fetch user config:', configError);
            }
            prepareAnalyticsIdentity(userData.id);
            publishAuthenticatedUser(userData as User);
            trackingService.identify(userData.id, {
              email: userData.email,
              name: userData.name,
              role: userData.roles?.[0] || "investor",
              isProvider: userData.roles?.includes("PROVIDER") || false,
              isAdmin: userData.roles?.includes("ADMIN") || false,
              providerId: userData.providerId,
              createdAt: userData.createdAt,
            });
            analyticsIdentityResetSinceLastIdentifyRef.current = false;
            trackAccountCreatedIfRecent({
              userId: userData.id,
              createdAt: userData.createdAt,
              method: "email_otp",
            });
          }

          trackAuth(flow === 'signup' ? 'signup' : 'login', 'verify-otp');
          setIsLoading(false);
          return {
            success: true,
            user: userData || undefined,
            token: accessToken,
          };
        }
      );
      if (loginResult.success) {
        saveLastLoginPreference({ method: "email", email: loginResult.user?.email ?? email });
      }
      if (oauthLogin) {
        navigateToOAuthResponseUrl(oauthLogin.url);
        return { success: true, redirected: true };
      }
      return loginResult;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      trackError(`OTP validation failed: ${errorMessage}`, 'AuthV2Context.completeLogin');
      trackingService.track(errorEvent, { reason: 'otp_verify_threw', message: errorMessage, flow });
      setIsLoading(false);
      return { success: false, message: errorMessage };
    }
  };

  const signInWithGoogle = async (idToken: string, accessToken?: string, oauthQuery?: string): Promise<LoginResponse> => {
    const sourceGeneration = accountContextGenerationRef.current;
    const sourceTokens = httpService.getAuthTokenPair();
    try {
      const anonymousId = getValidatedPostHogDeviceId();
      const loginRequest = {
        provider: "google" as const,
        idToken: { token: idToken },
        ...(anonymousId ? { anonymousId } : {}),
      };
      const oauthLogin = oauthQuery === undefined
        ? undefined
        : await completeOAuthLogin("/sign-in/social", loginRequest, oauthQuery);
      const response: SocialSignInResponse = oauthLogin ? { data: oauthLogin } : (await authClient.signIn.social(loginRequest)) as unknown as SocialSignInResponse;

      if (response?.error) {
        const message = response.error.status === 409
          && response.error.code === "ACCOUNT_DELETED"
          && response.error.deleted === true
          ? "This account is deleted. Contact an administrator to restore it."
          : response.error.message || "Google sign-in failed";
        trackError(`Google sign-in failed: ${message}`, "AuthContext.signInWithGoogle");
        trackingService.track(TrackingEvents.AUTH_GOOGLE_SSO_ERROR, { message });
        return { success: false, message };
      }

      const data = response?.data;
      const session = data?.session;
      const newAccessToken = session?.accessToken || data?.accessToken || session?.token || data?.token;
      const newRefreshToken = session?.refreshToken || data?.refreshToken;

      if (!newAccessToken || !newRefreshToken) {
        trackError("Google sign-in missing token pair", "AuthContext.signInWithGoogle");
        trackingService.track(TrackingEvents.AUTH_GOOGLE_SSO_ERROR, {
          message: "missing_token_pair",
        });
        return { success: false, message: "Google sign-in failed" };
      }

      const loginResult = await runInteractiveAuthTransition(
        sourceGeneration,
        sourceTokens,
        {
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        },
        async (transitionGeneration, prepareAnalyticsIdentity) => {
          let userData = session?.user || data?.user;
          if (!userData) {
            const userResponse = await httpService.getAuth<{ user: any }>(
              "/validate",
              { requestScope: "session-transition" }
            );
            userData = userResponse.status === 200
              ? userResponse.data?.user || {}
              : null;
          }

          if (userData) {
            if (userData.userProviders?.length) {
              userData.providerId = userData.userProviders[0].providerId;
            }
            if (!userData.roles || userData.roles.length === 0) {
              userData.roles = userData.userRoles?.length
                ? userData.userRoles.map((ur: any) => ur.role)
                : [];
            }

            settleCustomerViewConfig();
            try {
              const [config, accountContext] = await Promise.all([
                loadUserConfigForCurrentBoot("session-transition"),
                loadAccountContext(userData.id, false, "session-transition"),
              ]);
              if (isAccountTransitionInvalidated(transitionGeneration)) {
                throw new Error("Authentication changed in another browser tab");
              }
              setAccounts(accountContext.list);
              setEffectiveUserId(accountContext.effectiveUserId);
              setUserConfig(config);
              setUserConfigLoaded(true);
            } catch (configError) {
              if (isAccountTransitionInvalidated(transitionGeneration)) {
                throw configError;
              }
              console.error("Failed to fetch user config:", configError);
            }
            prepareAnalyticsIdentity(userData.id);
            publishAuthenticatedUser(userData as User);
            trackingService.identify(userData.id, {
              email: userData.email,
              name: userData.name,
              role: userData.roles?.[0] || "investor",
              isProvider: userData.roles?.includes("PROVIDER") || false,
              isAdmin: userData.roles?.includes("ADMIN") || false,
              providerId: userData.providerId,
              createdAt: userData.createdAt,
            });
            analyticsIdentityResetSinceLastIdentifyRef.current = false;
            trackAccountCreatedIfRecent({
              userId: userData.id,
              createdAt: userData.createdAt,
              method: "google_sso",
            });
            analyticsService.trackLoginSuccess(
              userData.id,
              userData.roles?.[0] || "investor",
              "google_sso"
            );
          }

          trackAuth("login", "google");
          trackingService.track(TrackingEvents.AUTH_GOOGLE_SSO_SUCCESS, {
            user_id: userData?.id,
          });
          return {
            success: true,
            user: userData || undefined,
            token: newAccessToken,
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          };
        }
      );
      if (loginResult.success) {
        saveLastLoginPreference({ method: "google" });
      }
      if (oauthLogin) {
        navigateToOAuthResponseUrl(oauthLogin.url);
        return { success: true, redirected: true };
      }
      return loginResult;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      trackError(`Google sign-in failed: ${errorMessage}`, "AuthContext.signInWithGoogle");
      return { success: false, message: errorMessage };
    }
  };


  const refreshAccounts = async (): Promise<void> => {
    const actorUserId = userRef.current?.id;
    if (!actorUserId) return;
    if (accountTransitionRef.current || externalTokenReconcilePendingRef.current) {
      throw new Error("An account-context transition is already in progress");
    }

    const requestGeneration = accountContextGenerationRef.current;
    const transitionOwner = claimAccountTransition();
    let httpTransitionStarted = false;
    setIsAccountContextLoading(true);
    try {
      const accountContext = await userAccountContextService.getAccounts({
        requestScope: "account-transition",
      });
      if (isAccountTransitionInvalidated(requestGeneration)) return;
      if (
        userRef.current?.id !== actorUserId
        || accountContext.userId !== actorUserId
      ) {
        await recoverFromStaleAccountContext();
        throw new Error("Account context actor does not match the authenticated user");
      }
      if (accountContext.effectiveUserId !== effectiveUserIdRef.current) {
        httpService.beginAccountContextTransition();
        httpTransitionStarted = true;
        stopTokenRefresh();
        const transitionGeneration = ++accountContextGenerationRef.current;
        setIsAccountSwitching(true);
        await blockUnverifiedAccountContext(effectiveUserIdRef.current);
        if (isAccountTransitionInvalidated(transitionGeneration)) return;
        const config = await loadUserConfigForCurrentBoot("account-transition");
        if (isAccountTransitionInvalidated(transitionGeneration)) return;
        setUserConfig(config);
        setUserConfigLoaded(true);
        setEffectiveUserId(accountContext.effectiveUserId);
      }
      setAccounts(accountContext.list);
    } finally {
      setIsAccountContextLoading(false);
      finishAccountTransition(
        httpTransitionStarted,
        true,
        httpTransitionStarted,
        transitionOwner
      );
    }
  };

  const handleDeniedAccountActivation = async (
    previousEffectiveUserId: string | null
  ): Promise<void> => {
    try {
      const confirmedContext = await userAccountContextService.getAccounts({
        requestScope: "account-transition",
      });
      const actorUserId = userRef.current?.id;
      if (!actorUserId || confirmedContext.userId !== actorUserId) {
        await recoverFromStaleAccountContext();
      } else if (confirmedContext.effectiveUserId !== previousEffectiveUserId) {
        await blockUnverifiedAccountContext(previousEffectiveUserId);
      } else {
        // The current context is still valid; only the requested target
        // became unavailable. Refresh choices without changing the token pair.
        setAccounts(confirmedContext.list);
      }
    } catch (error) {
      if (isAccountContextAccessDenied(error)) {
        // The same exact 403 from GET /v3/accounts proves the token's
        // existing effective grant is stale. Other failures do not.
        await recoverFromStaleAccountContext();
      } else {
        // The activation denial is ambiguous until the current context can be
        // verified. Preserve the actor's session tokens, but fail closed for
        // all account-owned UI and cached state until an explicit retry.
        await blockUnverifiedAccountContext(previousEffectiveUserId);
      }
    }
  };

  const activateAccount = async (targetUserId: string): Promise<void> => {
    const normalizedTargetUserId = targetUserId.trim();
    if (!normalizedTargetUserId) throw new Error("Target user ID is required");
    if (normalizedTargetUserId === effectiveUserIdRef.current) return;
    if (accountTransitionRef.current || externalTokenReconcilePendingRef.current) {
      throw new Error("An account-context transition is already in progress");
    }

    const previousTokens = httpService.getAuthTokenPair();
    if (!previousTokens) throw new Error("Authenticated token pair is unavailable");
    let rollbackTokens = previousTokens;
    const previousConfig = userConfig;
    const previousConfigLoaded = userConfigLoaded;
    const previousEffectiveUserId = effectiveUserIdRef.current;

    const transitionGeneration = ++accountContextGenerationRef.current;
    const transitionOwner = claimAccountTransition();
    httpService.beginAccountContextTransition();
    setIsAccountSwitching(true);
    stopTokenRefresh();
    let restartRefreshAfterTransition = true;
    try {
      let activation;
      try {
        activation = await userAccountContextService.activateAccount(
          normalizedTargetUserId,
          { requestScope: "account-transition" }
        );
        if (isAccountTransitionInvalidated(transitionGeneration)) {
          throw new Error("Account context changed in another browser tab");
        }
      } catch (error) {
        if (isAccountTransitionInvalidated(transitionGeneration)) {
          restartRefreshAfterTransition = false;
          await blockUnverifiedAccountContext(previousEffectiveUserId);
          throw error;
        }
        if (isAccountContextAccessDenied(error)) {
          await handleDeniedAccountActivation(previousEffectiveUserId);
        }
        throw error;
      }
      if (activation.account.userId !== normalizedTargetUserId) {
        throw new Error("Activated account does not match the requested target");
      }

      const currentTokens = httpService.getAuthTokenPair();
      if (!currentTokens) {
        await blockUnverifiedAccountContext(previousEffectiveUserId);
        throw new Error("Authenticated token pair is unavailable");
      }
      // Activation may legitimately refresh the prior context first. Roll back
      // to the latest observed pair, not the possibly expired pre-request pair.
      rollbackTokens = currentTokens;

      const activatedTokens = {
        accessToken: activation.accessToken,
        refreshToken: activation.refreshToken,
      };
      httpService.replaceAuthTokenPair(activatedTokens);
      setUserConfig(null);
      setUserConfigLoaded(customerViewBoot);

      let config: ProfileConfig | null;
      try {
        config = await loadUserConfigForCurrentBoot("account-transition");
      } catch (error) {
        if (isAccountTransitionInvalidated(transitionGeneration)) {
          restartRefreshAfterTransition = false;
          await blockUnverifiedAccountContext(previousEffectiveUserId);
          throw error;
        }
        if (isAccountContextAccessDenied(error)) {
          await recoverFromStaleAccountContext();
          throw error;
        }
        httpService.replaceAuthTokenPair(rollbackTokens);
        setUserConfig(previousConfig);
        setUserConfigLoaded(previousConfigLoaded);
        throw error;
      }
      if (isAccountTransitionInvalidated(transitionGeneration)) {
        restartRefreshAfterTransition = false;
        await blockUnverifiedAccountContext(previousEffectiveUserId);
        throw new Error("Account context changed in another browser tab");
      }

      await clearAccountScopedClientState(previousEffectiveUserId);
      if (isAccountTransitionInvalidated(transitionGeneration)) {
        restartRefreshAfterTransition = false;
        await blockUnverifiedAccountContext(previousEffectiveUserId);
        throw new Error("Account context changed in another browser tab");
      }
      setAccounts((current) => {
        const existingIndex = current.findIndex(
          (account) => account.userId === activation.account.userId
        );
        if (existingIndex === -1) return [...current, activation.account];
        return current.map((account, index) => (
          index === existingIndex ? activation.account : account
        ));
      });
      setUserConfig(config);
      setUserConfigLoaded(true);
      setEffectiveUserId(activation.account.userId);
    } finally {
      finishAccountTransition(
        restartRefreshAfterTransition,
        true,
        true,
        transitionOwner
      );
    }
  };

  const runInActorAccountContext = async <T,>(
    operation: () => Promise<T>
  ): Promise<T> => {
    const actorUserId = userRef.current?.id ?? null;
    if (!actorUserId) throw new Error("Authenticated actor is unavailable");
    if (effectiveUserIdRef.current === actorUserId) return operation();
    if (accountTransitionRef.current || externalTokenReconcilePendingRef.current) {
      throw new Error("An account-context transition is already in progress");
    }

    const previousEffectiveUserId = effectiveUserIdRef.current;
    const transitionGeneration = ++accountContextGenerationRef.current;
    const transitionOwner = claimAccountTransition();
    httpService.beginAccountContextTransition();
    setIsAccountSwitching(true);
    stopTokenRefresh();
    let restartRefreshAfterTransition = true;
    try {
      let activation;
      try {
        activation = await userAccountContextService.activateAccount(actorUserId, {
          requestScope: "account-transition",
        });
        if (isAccountTransitionInvalidated(transitionGeneration)) {
          throw new Error("Account context changed in another browser tab");
        }
      } catch (error) {
        if (isAccountTransitionInvalidated(transitionGeneration)) {
          restartRefreshAfterTransition = false;
          await blockUnverifiedAccountContext(previousEffectiveUserId);
          throw error;
        }
        if (isAccountContextAccessDenied(error)) {
          await handleDeniedAccountActivation(previousEffectiveUserId);
        }
        throw error;
      }
      if (activation.account.userId !== actorUserId) {
        throw new Error("Self activation returned a different account");
      }

      // From this point forward the delegated pair is deliberately never
      // restored. The callback may revoke that exact grant even when its
      // response is lost, so self is the only deterministic recovery context.
      const selfTokens = {
        accessToken: activation.accessToken,
        refreshToken: activation.refreshToken,
      };
      httpService.replaceAuthTokenPair(selfTokens);
      setUserConfig(null);
      setUserConfigLoaded(customerViewBoot);

      let result: T | undefined;
      let operationError: unknown;
      try {
        result = await operation();
      } catch (error) {
        operationError = error;
      }

      let accountContext: GetAccountsResponse;
      let config: ProfileConfig | null;
      try {
        [accountContext, config] = await Promise.all([
          userAccountContextService.getAccounts({
            requestScope: "account-transition",
          }),
          loadUserConfigForCurrentBoot("account-transition"),
        ]);
        if (
          isAccountTransitionInvalidated(transitionGeneration)
          || userRef.current?.id !== actorUserId
          || accountContext.userId !== actorUserId
          || accountContext.effectiveUserId !== actorUserId
        ) {
          throw new Error("Self account context could not be verified");
        }
      } catch (contextError) {
        if (isAccountTransitionInvalidated(transitionGeneration)) {
          restartRefreshAfterTransition = false;
          await blockUnverifiedAccountContext(previousEffectiveUserId);
          throw operationError ?? contextError;
        }
        await clearAccountScopedClientState(previousEffectiveUserId);
        setAccounts([activation.account]);
        setEffectiveUserId(actorUserId);
        // Self tokens are valid, but effective-user configuration is unknown.
        // Keep config-gated UI blocked until refreshUserConfig succeeds.
        setUserConfig(null);
        setUserConfigLoaded(customerViewBoot);
        throw operationError ?? contextError;
      }
      await clearAccountScopedClientState(previousEffectiveUserId);
      if (isAccountTransitionInvalidated(transitionGeneration)) {
        restartRefreshAfterTransition = false;
        await blockUnverifiedAccountContext(previousEffectiveUserId);
        throw operationError ?? new Error("Account context changed in another browser tab");
      }
      setAccounts(accountContext.list);
      setUserConfig(config);
      setUserConfigLoaded(true);
      setEffectiveUserId(accountContext.effectiveUserId);

      if (operationError) throw operationError;
      return result as T;
    } finally {
      finishAccountTransition(
        restartRefreshAfterTransition,
        true,
        true,
        transitionOwner
      );
    }
  };


  const logout = async (): Promise<void> => {
    const logoutTokens = httpService.getAuthTokenPair();
    const logoutGeneration = ++accountContextGenerationRef.current;
    const transitionOwner = claimAccountTransition();
    httpService.beginAccountContextTransition();
    httpService.beginAuthSessionTransition();
    setIsAccountSwitching(true);
    stopTokenRefresh();
    try {
      if (logoutTokens) {
        await httpService.postAuth('/logout', undefined, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${logoutTokens.accessToken}`,
          },
          requiresAuth: false,
          requestScope: "session-transition",
        });
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      try {
        // Clear local state regardless of API call success
        await clearAccountScopedClientState(effectiveUserIdRef.current);
        if (userRef.current?.id && userRef.current.id !== effectiveUserIdRef.current) {
          clearAlphaIbkrAccountDetailsDraft(userRef.current.id);
          clearAlphaRiskProfileDraft(userRef.current.id);
          clearAlphaAllocationDraft(userRef.current.id);
        }
        queryClient.clear();
        const logoutStillOwnsTokens = (
          accountContextGenerationRef.current === logoutGeneration
          && !httpService.hasUnadoptedExternalAuthTokenChange()
        );
        if (logoutStillOwnsTokens) httpService.clearAuthTokenPair();
        setAccounts([]);
        setEffectiveUserId(null);
        setIsAccountContextLoading(false);
        clearAdminCustomerViewRouteStore();
        userRef.current = null;
        setUser(null);
        setUserConfig(null);
        setUserConfigLoaded(false);
        trackAuth('logout', 'manual-v2');
        trackingService.track(TrackingEvents.AUTH_LOGOUT, { method: 'manual' });
        // Reset analytics identity so the next user on a shared device starts fresh
        // and persisted capture state is cleared.
        resetAnalyticsIdentity();
      } finally {
        httpService.finishAuthSessionTransition();
        finishAccountTransition(false, true, true, transitionOwner);
      }
    }
  };

  reconcileExternalTokensRef.current = async (): Promise<void> => {
    if (!externalTokenReconcilePendingRef.current) return;
    if (authInitializationInFlightRef.current) return;
    if (staleRecoveryPromiseRef.current) return;
    const actor = userRef.current;
    if (accountTransitionRef.current) return;
    externalTokenReconcilePendingRef.current = false;
    if (!httpService.getAuthTokenPair()) {
      await recoverFromStaleAccountContext();
      return;
    }

    const previousEffectiveUserId = effectiveUserIdRef.current;
    const reconciliationGeneration = accountContextGenerationRef.current;
    const transitionOwner = claimAccountTransition();
    setIsAccountSwitching(true);
    setIsAccountContextLoading(true);
    stopTokenRefresh();
    settleCustomerViewConfig();
    let reconciliationSucceeded = false;
    try {
      const actorResponse = await httpService.getAuth<{ user: any }>("/validate", {
        requestScope: "session-transition",
      });
      if (isAccountTransitionInvalidated(reconciliationGeneration)) return;
      const reconciledActor = normalizeAuthenticatedUser(actorResponse.data?.user || {});
      if (!reconciledActor.id) {
        throw new Error("Externally changed session did not resolve an authenticated actor");
      }
      const actorChanged = reconciledActor.id !== actor?.id;
      if (actorChanged) {
        userRef.current = null;
        setUser(null);
        resetAnalyticsIdentity();
        await blockUnverifiedAccountContext(previousEffectiveUserId);
        if (actor?.id && actor.id !== previousEffectiveUserId) {
          clearAlphaIbkrAccountDetailsDraft(actor.id);
          clearAlphaRiskProfileDraft(actor.id);
          clearAlphaAllocationDraft(actor.id);
        }
        queryClient.clear();
        clearAdminCustomerViewRouteStore();
        // Do not adopt a different browser-wide actor inside an established
        // React tree: non-account caches may still belong to the old session.
        // Preserve the external pair so a normal reload can rehydrate it.
        return;
      }
      if (isAccountTransitionInvalidated(reconciliationGeneration)) return;

      const [accountContext, config] = await Promise.all([
        userAccountContextService.getAccounts({
          requestScope: "session-transition",
        }),
        loadUserConfigForCurrentBoot("session-transition"),
      ]);
      if (isAccountTransitionInvalidated(reconciliationGeneration)) return;
      if (accountContext.userId !== reconciledActor.id) {
        throw new Error("Account context actor does not match the externally authenticated user");
      }

      if (!actorChanged && accountContext.effectiveUserId !== previousEffectiveUserId) {
        await clearAccountScopedClientState(previousEffectiveUserId);
      }
      if (isAccountTransitionInvalidated(reconciliationGeneration)) return;
      setAccounts(accountContext.list);
      setUserConfig(config);
      setUserConfigLoaded(true);
      setEffectiveUserId(accountContext.effectiveUserId);
      reconciliationSucceeded = true;
    } catch (error) {
      if (isAccountTransitionInvalidated(reconciliationGeneration)) return;
      // The external pair may belong to a different actor. Preserve it for a
      // reload/retry, but fail closed locally rather than retaining old roles.
      await blockUnverifiedAccountContext(previousEffectiveUserId);
      queryClient.clear();
      clearAdminCustomerViewRouteStore();
      userRef.current = null;
      setUser(null);
      resetAnalyticsIdentity();
      console.error("Failed to reconcile browser-wide account context:", error);
    } finally {
      setIsAccountContextLoading(false);
      finishAccountTransition(
        reconciliationSucceeded,
        true,
        false,
        transitionOwner
      );
    }
  };

  useEffect(() => {
    let reconcileTimer: ReturnType<typeof setTimeout> | null = null;
    const handleStorage = (event: StorageEvent) => {
      if (
        event.storageArea !== localStorage
        || (
          event.key !== "plutus_auth_access_token"
          && event.key !== "plutus_auth_refresh_token"
        )
      ) return;

      beginExternalHttpTransition();
      httpService.notifyExternalAuthTokenChange();
      accountContextGenerationRef.current += 1;
      externalTokenReconcilePendingRef.current = true;
      setIsAccountSwitching(true);
      stopTokenRefresh();
      if (reconcileTimer) clearTimeout(reconcileTimer);
      // Both token writes happen synchronously. Deferring one task coalesces
      // their two storage events so another tab never reads a half-updated pair.
      reconcileTimer = setTimeout(() => {
        reconcileTimer = null;
        void reconcileExternalTokensRef.current();
      }, 0);
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      if (reconcileTimer) clearTimeout(reconcileTimer);
      externalTokenReconcilePendingRef.current = false;
      finishExternalHttpTransition();
    };
  }, []);

  // Role checking utility methods
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || false;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => user?.roles?.includes(role)) || false;
  };

  const isAdmin = (): boolean => {
    return hasRole('ADMIN');
  };

  const getDefaultLandingPage = (origin?: "homepage"): string => {
    // Hierarchy: ADMIN > PROVIDER > ADVISOR > INVESTOR
    if (hasRole('ADMIN')) {
      return '/admin/dashboard';
    }
    if (hasRole('PROVIDER')) {
      return '/provider/portfolios';
    }
    if (hasRole('ADVISOR')) {
      return '/advisor/clients';
    }
    // Connected brokerages land on Dashboard; other prospects enter Discover.
    // tradingenabled does not affect login routing.
    const isCustomer = getUserConfigValue('iscustomer') === 'true';
    if (isCustomer || getUserConfigValue('isbeta') === 'true') {
      return '/alpha/dashboard';
    }
    return '/alpha/discover';
  };

  const getUserConfigValue = (key: string): string | null => {
    return coerceUserConfigValue(userConfig?.data?.[key]);
  };

  const refreshUserConfig = async (): Promise<void> => {
    if (customerViewBoot) {
      setUserConfig(null);
      setUserConfigLoaded(true);
      return;
    }
    if (accountTransitionRef.current || externalTokenReconcilePendingRef.current) return;
    const requestId = ++userConfigRequestRef.current;
    const requestGeneration = accountContextGenerationRef.current;
    const requestActorUserId = userRef.current?.id ?? null;
    const requestEffectiveUserId = effectiveUserIdRef.current;
    const config = await loadUserConfigForCurrentBoot();
    if (
      requestId !== userConfigRequestRef.current
      || requestGeneration !== accountContextGenerationRef.current
      || requestActorUserId !== (userRef.current?.id ?? null)
      || requestEffectiveUserId !== effectiveUserIdRef.current
    ) return;
    setUserConfig(config);
    setUserConfigLoaded(true);
  };

  const value: AuthContextType = {
    user,
    actorUserId: user?.id ?? null,
    effectiveUserId,
    accounts,
    isAccountContextLoading,
    isAccountSwitching,
    userConfig,
    userConfigLoaded,
    isAuthenticated: !!user,
    isLoading,
    logout,
    hasRole,
    hasAnyRole,
    isAdmin,
    getDefaultLandingPage,
    sendOtp,
    verifyOtp,
    signInWithGoogle,
    getUserConfigValue,
    refreshUserConfig,
    refreshAccounts,
    activateAccount,
    runInActorAccountContext,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
